import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTags = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '' });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tags`);
      setTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/tags`, formData, {
        withCredentials: true
      });
      setFormData({ name: '', slug: '' });
      setShowForm(false);
      fetchTags();
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Failed to create tag');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/tags/${id}`, {
        withCredentials: true
      });
      setTags(tags.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Failed to delete tag');
    }
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="admin-tags-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl font-bold">Tags</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="toggle-tag-form"
          className="flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Tag</span>
        </Button>
      </div>

      {showForm && (
        <div className="bg-card border border-border p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <Input
                data-testid="tag-name-input"
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData({ name, slug: generateSlug(name) });
                }}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Slug</label>
              <Input
                data-testid="tag-slug-input"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
              />
            </div>
            <div className="flex space-x-3">
              <Button type="submit" data-testid="tag-submit-btn">
                Create Tag
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border border-border">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Name</th>
              <th className="text-left p-4 font-semibold">Slug</th>
              <th className="text-left p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.length === 0 ? (
              <tr>
                <td colSpan="3" className="p-8 text-center text-muted-foreground">
                  No tags yet
                </td>
              </tr>
            ) : (
              tags.map((tag) => (
                <tr key={tag.id} data-testid={`tag-row-${tag.id}`} className="border-b border-border hover:bg-muted">
                  <td className="p-4 font-medium">{tag.name}</td>
                  <td className="p-4">{tag.slug}</td>
                  <td className="p-4">
                    <button
                      onClick={() => handleDelete(tag.id)}
                      data-testid={`delete-tag-${tag.id}`}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTags;